import React, { useState } from 'react';
import { useQuery, useMutation } from '@/apollo';
import { toast } from 'sonner';
import { GET_CUSTOMERS, CREATE_CUSTOMER, UPDATE_CUSTOMER, DELETE_CUSTOMER } from '../../graphql/customers';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Building,
  // Users, // Reserved for future use
  // Calendar, // Reserved for future use
  AlertCircle
} from 'lucide-react';
import { ConfirmModal } from '../../framework/modals/ConfirmModal';
import { FormModal } from './shared/FormModal';
import { BaseUtils } from '../../utils/BaseUtils';
import { SortableTableHeader } from './shared/SortableTableHeader';
import { Pagination } from './shared/Pagination';
import { SearchBar } from './shared/SearchBar';
import { FormInput } from './shared/FormInput';
import { FormTextarea } from './shared/FormTextarea';
import { FormCheckbox } from './shared/FormCheckbox';
import { useTableSort } from './hooks/useTableSort';
import { useModeratorPermissions } from './hooks/useModeratorPermissions';
import { useAuth } from '../../contexts/AuthContext';

interface Customer {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  providers: Array<{ id: string }>;
  locations: Array<{ id: string }>;
}

interface EnhancedCustomerManagementProps {
  onBack?: () => void;
}

export const EnhancedCustomerManagement: React.FC<EnhancedCustomerManagementProps> = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMINISTRATOR';
  const { canChange } = useModeratorPermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ isOpen: boolean; customerId: string | null }>({
    isOpen: false,
    customerId: null
  });


  // Form state
  const [customerForm, setCustomerForm] = useState({
    name: '',
    description: '',
    isActive: true
  });

  // GraphQL queries and mutations
  const { data: customersData, loading: customersLoading, error: customersError, refetch: refetchCustomers } = useQuery(GET_CUSTOMERS, { 
    fetchPolicy: 'network-only' 
  });

  const [createCustomer] = useMutation(CREATE_CUSTOMER, {
    onCompleted: () => {
      setShowCustomerForm(false);
      setEditingCustomer(null);
      setCustomerForm({ name: '', description: '', isActive: true });
      refetchCustomers();
    }
  });

  const [updateCustomer] = useMutation(UPDATE_CUSTOMER, {
    onCompleted: () => {
      setShowCustomerForm(false);
      setEditingCustomer(null);
      setCustomerForm({ name: '', description: '', isActive: true });
      refetchCustomers();
    }
  });

  const [deleteCustomer] = useMutation(DELETE_CUSTOMER, {
    onCompleted: () => {
      toast.success('Customer deleted successfully');
      refetchCustomers();
    },
    onError: (error) => {
      console.error('[DELETE CUSTOMER] Mutation error:', error);
      
      // Extract user-friendly error message
      const errorMessage = error.message || 'Failed to delete customer';
      
      // Show specific message for providers constraint
      if (errorMessage.includes('associated providers')) {
        toast.error('Cannot delete customer', {
          description: 'This customer has associated providers. Please remove all providers before deleting the customer.',
          duration: 5000,
        });
      } else {
        toast.error('Failed to delete customer', {
          description: errorMessage,
          duration: 5000,
        });
      }
    }
  });

  // Filter customers
  const filteredCustomers = React.useMemo(() => {
    if (!customersData?.customers) return [];

    return customersData.customers.filter((customer: Customer) => {
      return customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
             customer.description?.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [customersData?.customers, searchTerm]);

  // Table sorting and pagination
  const {
    paginatedData,
    sortField,
    sortDirection,
    currentPage,
    totalPages,
    itemsPerPage,
    handleSort,
    setCurrentPage,
    setItemsPerPage
  } = useTableSort(filteredCustomers, 'name', 10);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingCustomer) {
      updateCustomer({
        variables: {
          id: editingCustomer.id,
          input: customerForm
        }
      });
    } else {
      createCustomer({
        variables: {
          input: customerForm
        }
      });
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Customer Management
        </h2>
        {isAdmin && (
        <button
          onClick={() => {
            setEditingCustomer(null);
            setCustomerForm({ name: '', description: '', isActive: true });
            setShowCustomerForm(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors"
          style={{
            backgroundColor: 'var(--color-accent)',
            color: 'var(--color-text-on-accent)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-accent)';
          }}
        >
          <Plus className="w-4 h-4" />
          <span>Add Customer</span>
        </button>
        )}
      </div>

      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search customers..."
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={setItemsPerPage}
        totalItems={filteredCustomers.length}
      />

      {customersLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--color-accent)' }}></div>
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading customers...</p>
        </div>
      ) : customersError ? (
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-error)' }} />
          <p style={{ color: 'var(--color-error)' }}>Error loading customers: {customersError.message}</p>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="text-center py-12">
          <Building className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
          <p style={{ color: 'var(--color-text-secondary)' }}>
            {customersData?.customers?.length === 0 ? 'No customers added yet' : 'No customers match your search'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                <SortableTableHeader
                  field="name"
                  label="Name"
                  currentSortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <th className="text-left p-3" style={{ color: 'var(--color-text-secondary)' }}>Description</th>
                <SortableTableHeader
                  field="providers"
                  label="Providers"
                  currentSortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  field="locations"
                  label="Locations"
                  currentSortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  field="isActive"
                  label="Status"
                  currentSortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  field="createdAt"
                  label="Created"
                  currentSortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <th className="text-right p-3" style={{ color: 'var(--color-text-secondary)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(paginatedData as Customer[]).map((customer) => (
                <tr key={customer.id} className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <td className="p-3" style={{ color: 'var(--color-text-primary)' }}>
                    <div className="font-medium">{customer.name}</div>
                  </td>
                  <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>
                    {customer.description || '-'}
                  </td>
                  <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>
                    {customer.providers.length}
                  </td>
                  <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>
                    {customer.locations?.length || 0}
                  </td>
                  <td className="p-3">
                    <span
                      className="px-2 py-1 rounded text-xs"
                      style={{
                        backgroundColor: customer.isActive ? 'var(--color-success-bg)' : 'var(--color-error-bg)',
                        color: customer.isActive ? 'var(--color-success)' : 'var(--color-error)'
                      }}
                    >
                      {customer.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="p-3" style={{ color: 'var(--color-text-secondary)' }}>
                    {BaseUtils.formatDate(new Date(customer.createdAt), 'MMM DD, YYYY')}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end space-x-2">
                      {canChange(customer.id) && (
                      <button
                        onClick={() => {
                          setEditingCustomer(customer);
                          setCustomerForm({
                            name: customer.name,
                            description: customer.description || '',
                            isActive: customer.isActive
                          });
                          setShowCustomerForm(true);
                        }}
                        className="p-2 rounded hover:bg-opacity-10"
                        style={{ color: 'var(--color-accent)' }}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      )}
                      {isAdmin && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          // Check if customer has providers
                          if (customer.providers.length > 0) {
                            toast.error('Cannot delete customer', {
                              description: `This customer has ${customer.providers.length} associated provider(s). Please remove all providers before deleting the customer.`,
                              duration: 5000,
                            });
                            return;
                          }
                          
                          console.log('[DELETE] Delete button clicked for customer:', customer.id);
                          // Double-check window.confirm is overridden
                          const confirmStr = window.confirm.toString();
                          if (!confirmStr.includes('[BLOCKED]')) {
                            console.error('[DELETE] WARNING: window.confirm override is NOT active!', confirmStr);
                          }
                          setDeleteConfirmModal({ isOpen: true, customerId: customer.id });
                        }}
                        className="p-2 rounded hover:bg-opacity-10"
                        style={{ 
                          color: customer.providers.length > 0 ? 'var(--color-text-tertiary, #9ca3af)' : 'var(--color-error)',
                          cursor: customer.providers.length > 0 ? 'not-allowed' : 'pointer',
                          opacity: customer.providers.length > 0 ? 0.5 : 1
                        }}
                        disabled={customer.providers.length > 0}
                        aria-label={`Delete customer ${customer.name}${customer.providers.length > 0 ? ' (disabled: has providers)' : ''}`}
                        title={customer.providers.length > 0 ? `Cannot delete: Customer has ${customer.providers.length} associated provider(s)` : 'Delete customer'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <FormModal
        isOpen={showCustomerForm}
        onClose={() => setShowCustomerForm(false)}
        title={editingCustomer ? 'Edit Customer' : 'Add Customer'}
        subtitle={editingCustomer ? 'Update customer organization details' : 'Create a new customer organization'}
        icon={Building}
        maxWidth="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput
            label="Name"
            required
            type="text"
            value={customerForm.name}
            onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
            backgroundColor="primary"
          />

          <FormTextarea
            label="Description"
            value={customerForm.description}
            onChange={(e) => setCustomerForm({ ...customerForm, description: e.target.value })}
            rows={3}
            backgroundColor="primary"
          />

          <FormCheckbox
            label="Active"
            checked={customerForm.isActive}
            onChange={(e) => setCustomerForm({ ...customerForm, isActive: e.target.checked })}
            backgroundColor="primary"
          />

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={() => setShowCustomerForm(false)}
              className="px-4 py-2 border rounded-lg transition-colors"
              style={{
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-secondary)'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg transition-colors"
              style={{
                backgroundColor: 'var(--color-accent)',
                color: 'var(--color-text-on-accent)'
              }}
            >
              {editingCustomer ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </FormModal>

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={deleteConfirmModal.isOpen}
          onClose={() => {
            setDeleteConfirmModal({ isOpen: false, customerId: null });
          }}
          onConfirm={async () => {
            if (deleteConfirmModal.customerId) {
              try {
                await deleteCustomer({ variables: { id: deleteConfirmModal.customerId } });
                setDeleteConfirmModal({ isOpen: false, customerId: null });
              } catch (error) {
                console.error('[DELETE CUSTOMER] Delete error:', error);
                // Don't close modal on error - let user retry
              }
            }
          }}
          title="Delete Customer"
          message="Are you sure you want to delete this customer? This action cannot be undone."
          type="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
        />
    </div>
  );
};
